import { formatDate, translate, useLocale } from "../localization";
import { enUS, es, arSA, zhCN, vi, ptBR, tr, fr, it, de, ru, hi } from "date-fns/locale";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import DatePicker from "react-datepicker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangePicker.scss";
import { AppButton } from "./ui";

const calendarLocales = { en: enUS, "es-419": es, ar: arSA, "zh-CN": zhCN, vi, "pt-BR": ptBR, tr, fr, it, de, ru, hi };

const RangeFields = forwardRef(
  ({ startLabel, endLabel, startText, endText, onClick }, ref) => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.(event);
      }
    };

    return (
      <div className="rangeFields" ref={ref}>
        <div className="rangeField" onClick={onClick} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
          <div className="rangeFieldLabel">{startLabel}</div>
          <div className="rangeFieldValue">{startText}</div>
          <div className="rangeFieldUnderline" />
        </div>

        <div className="rangeField" onClick={onClick} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
          <div className="rangeFieldLabel">{endLabel}</div>
          <div className="rangeFieldValue">{endText}</div>
          <div className="rangeFieldUnderline" />
        </div>
      </div>
    );
  }
);

const DateRangePicker = ({ onRangeUpdated, dateRange, compact = false }) => {
  const locale = useLocale();
  const ranges = useMemo(
    () => [
      { label: translate("Last day"), value: 1 },
      { label: translate("Past week"), value: 7 },
      { label: translate("Past month"), value: 30 },
      { label: translate("Three months"), value: 90 },
    ],
    [locale]
  );

  const [selected, setSelected] = useState(2);
  const [startDate, setStartDate] = useState(dateRange?.start ?? null); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState(dateRange?.end ?? null); // "YYYY-MM-DD"
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);
  const [calendarPanelOpen, setCalendarPanelOpen] = useState(false);
  const compactRootRef = useRef(null);
  const lastEmittedRangeRef = useRef("");
  const incomingStart = dateRange?.start ?? null;
  const incomingEnd = dateRange?.end ?? null;

  useEffect(() => {
    setStartDate(incomingStart);
    setEndDate(incomingEnd);
  }, [incomingStart, incomingEnd]);

  const applyPreset = (days, index) => {
    const formattedStart = moment()
      .subtract(days, "days")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD");

    const formattedEnd = moment().endOf("day").utc().format("YYYY-MM-DD");

    setStartDate(formattedStart);
    setEndDate(formattedEnd);
    setSelected(index);
  };

  useEffect(() => {
    if (!compact) return undefined;

    const handleDocumentMouseDown = (event) => {
      if (!compactRootRef.current?.contains(event.target)) {
        setQuickPanelOpen(false);
        setCalendarPanelOpen(false);
      }
    };

    const handleDocumentKeydown = (event) => {
      if (event.key === "Escape") {
        setQuickPanelOpen(false);
        setCalendarPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    document.addEventListener("keydown", handleDocumentKeydown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      document.removeEventListener("keydown", handleDocumentKeydown);
    };
  }, [compact]);

  useEffect(() => {
    const initial = ranges[selected];
    if (initial) applyPreset(initial.value, selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onRangeUpdated) return;
    if (!startDate || !endDate) return;

    const emittedRangeKey = `${startDate}|${endDate}`;
    if (lastEmittedRangeRef.current === emittedRangeKey) return;

    lastEmittedRangeRef.current = emittedRangeKey;
    onRangeUpdated({ start: startDate, end: endDate });
  }, [startDate, endDate, onRangeUpdated]);

  const startAsDate = startDate ? moment(startDate, "YYYY-MM-DD").toDate() : null;
  const endAsDate = endDate ? moment(endDate, "YYYY-MM-DD").toDate() : null;

  const startText = startAsDate ? formatDate(startAsDate) : "—";
  const endText = endAsDate ? formatDate(endAsDate) : "—";
  const compactDateRangeText =
    startAsDate && endAsDate
      ? `${formatDate(startAsDate, { month: "short", day: "numeric" })} – ${formatDate(endAsDate, { month: "short", day: "numeric" })}`
      : translate("Choose dates");
  const quickRangeText = selected >= 0 ? ranges[selected]?.label || translate("Quick range") : translate("Custom range");

  const renderButtons = ranges.map((range, i) => {
    const isSelected = i === selected;
    return (
      <AppButton
        key={range.value}
        className={`date-range-preset-button ${isSelected ? "selected" : ""}`}
        onClick={() => applyPreset(range.value, i)}
        type="button"
        kind="secondary"
        selected={isSelected}
      >
        {range.label}
      </AppButton>
    );
  });

  if (compact) {
    return (
      <div className="date-range-picker date-range-picker--compact" ref={compactRootRef}>
        <div className="date-range-compact-controls">
          <div className={`date-range-panel-host ${quickPanelOpen ? "is-open" : ""}`}>
            <AppButton
              type="button"
              kind="secondary"
              className="date-range-trigger date-range-trigger--quick"
              selected={quickPanelOpen}
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
              onClick={() => {
                setQuickPanelOpen((open) => !open);
                setCalendarPanelOpen(false);
              }}
            >
              {quickRangeText}
            </AppButton>

            {quickPanelOpen && (
              <div className="quick-range-panel" role="menu" aria-label={translate("Quick date ranges")}>
                {ranges.map((range, i) => {
                  const isSelected = i === selected;
                  return (
                    <AppButton
                      key={range.value}
                      type="button"
                      kind="secondary"
                      selected={isSelected}
                      className="date-range-preset-option"
                      onClick={() => {
                        applyPreset(range.value, i);
                        setQuickPanelOpen(false);
                      }}
                    >
                      {range.label}
                    </AppButton>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`date-range-panel-host ${calendarPanelOpen ? "is-open" : ""}`}>
            <AppButton
              type="button"
              kind="secondary"
              className="date-range-trigger date-range-trigger--calendar"
              selected={calendarPanelOpen}
              startIcon={<CalendarMonthIcon fontSize="small" />}
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
              onClick={() => {
                setCalendarPanelOpen((open) => !open);
                setQuickPanelOpen(false);
              }}
            >
              {compactDateRangeText}
            </AppButton>

            {calendarPanelOpen && (
              <div className="calendar-panel">
                <DatePicker
                  locale={calendarLocales[locale]}
                  previousMonthAriaLabel={translate("Previous month")}
                  nextMonthAriaLabel={translate("Next month")}
                  chooseDayAriaLabelPrefix={translate("Choose")}
                  inline
                  selectsRange
                  startDate={startAsDate}
                  endDate={endAsDate}
                  onChange={(dates) => {
                    const [start, end] = dates;

                    const formattedStart = start
                      ? moment(start).utc().startOf("day").format("YYYY-MM-DD")
                      : null;

                    const formattedEnd = end ? moment(end).utc().endOf("day").format("YYYY-MM-DD") : null;

                    setStartDate(formattedStart);
                    setEndDate(formattedEnd);
                    setSelected(-1);

                    if (formattedStart && formattedEnd) {
                      setCalendarPanelOpen(false);
                    }
                  }}
                  maxDate={new Date()}
                  monthsShown={1}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="date-range-picker date-range-picker--full">
      <div className="date-range-group">
        <div className="range-picker">{renderButtons}</div>

        <div className="date-range-inline">
          <DatePicker
            locale={calendarLocales[locale]}
            previousMonthAriaLabel={translate("Previous month")}
            nextMonthAriaLabel={translate("Next month")}
            chooseDayAriaLabelPrefix={translate("Choose")}
            selectsRange
            startDate={startAsDate}
            endDate={endAsDate}
            onChange={(dates) => {
              const [start, end] = dates;

              const formattedStart = start
                ? moment(start).utc().startOf("day").format("YYYY-MM-DD")
                : null;

              const formattedEnd = end
                ? moment(end).utc().endOf("day").format("YYYY-MM-DD")
                : null;

              setStartDate(formattedStart);
              setEndDate(formattedEnd);
              setSelected(-1);
            }}
            maxDate={new Date()}
            monthsShown={2}
            shouldCloseOnSelect={false}
            popperPlacement="bottom"
            customInput={
              <RangeFields
                startLabel={translate("START")}
                endLabel={translate("END")}
                startText={startText}
                endText={endText}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
